import { Select } from '@round/ui-kit';
import { InfluencerLocationOption, useInfluencerLocationSelect } from 'hooks/useInfluencerLocationSelect';
import { waveSelectStyles } from '../waveSelectStyles';

type Option = InfluencerLocationOption | null;

export type LocationValue = {
    country: Option;
    state: Option;
    city: Option;
};

export const emptyLocationValue: LocationValue = { country: null, state: null, city: null };

export const locationIdOf = (value: LocationValue): number | null =>
    value.city?.value ?? value.state?.value ?? value.country?.value ?? null;

type LocationSelectProps = {
    inputId: string;
    type: 'country' | 'state' | 'city';
    placeholder: string;
    value: Option;
    onChange: (option: Option) => void;
    country_code?: string;
    state?: string;
};

// Turn an ISO country code (e.g. "US") into its flag emoji.
const flagEmoji = (countryCode: string): string =>
    countryCode.toUpperCase().replace(/[A-Z]/g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));

// The shared option label is "Country, City, State". Each picker only needs the
// part it's choosing: country (with flag), just the state, or just the city.
const formatOptionLabelFor = (type: LocationSelectProps['type']) => {
    if (type === 'country') {
        return (option: InfluencerLocationOption) => `${flagEmoji(option.countryCode)} ${option.country}`;
    }
    if (type === 'state') {
        return (option: InfluencerLocationOption) => option.state || option.label;
    }
    return (option: InfluencerLocationOption) => option.city || option.label;
};

// Surface the most common creator countries first; everything else keeps its
// alphabetical order.
const PRIORITY_COUNTRY_CODES = ['US', 'GB'];
const countryPriority = (countryCode: string): number => {
    const index = PRIORITY_COUNTRY_CODES.indexOf(countryCode);
    return index === -1 ? PRIORITY_COUNTRY_CODES.length : index;
};

const LocationSelect = ({ inputId, type, placeholder, value, onChange, country_code, state }: LocationSelectProps) => {
    const { selectProps } = useInfluencerLocationSelect({
        type,
        country_code,
        state,
        ordering: 'country_name',
        // Load the full (small) country list so US/UK can be pinned to the top regardless of paging.
        pageSize: type === 'country' ? 300 : undefined,
    });
    const options =
        type === 'country'
            ? [...selectProps.options].sort((a, b) => countryPriority(a.countryCode) - countryPriority(b.countryCode))
            : selectProps.options;
    return (
        <Select
            {...selectProps}
            options={options}
            styles={waveSelectStyles}
            formatOptionLabel={formatOptionLabelFor(type)}
            inputId={inputId}
            placeholder={placeholder}
            value={value}
            onChange={(option) => onChange((option as Option) ?? null)}
        />
    );
};

type Props = {
    value: LocationValue;
    onChange: (value: LocationValue) => void;
};

/**
 * Cascading country → (US) state → optional city picker over existing Location rows.
 * Controlled by the parent so selections survive step navigation (Back/Next).
 */
export const LocationStep = ({ value, onChange }: Props) => {
    const { country, state, city } = value;
    const isUnitedStates = country?.countryCode === 'US';

    return (
        <div className="flex flex-col gap-4">
            <div className="wm-field">
                <label className="wm-field__label" htmlFor="location-country">
                    <span>
                        Country <span className="text-wm-danger">*</span>
                    </span>
                </label>
                <LocationSelect
                    inputId="location-country"
                    type="country"
                    placeholder="Select your country"
                    value={country}
                    onChange={(option) => onChange({ country: option, state: null, city: null })}
                />
            </div>

            {country && isUnitedStates && (
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor="location-state">
                        State (optional)
                    </label>
                    <LocationSelect
                        key={`state-${country.value}`}
                        inputId="location-state"
                        type="state"
                        country_code="US"
                        placeholder="Select your state"
                        value={state}
                        onChange={(option) => onChange({ country, state: option, city: null })}
                    />
                </div>
            )}

            {country && (
                <div className="wm-field">
                    <label className="wm-field__label" htmlFor="location-city">
                        City (optional)
                    </label>
                    <LocationSelect
                        key={`city-${state?.value ?? country.value}`}
                        inputId="location-city"
                        type="city"
                        country_code={country.countryCode}
                        state={state?.state}
                        placeholder="Select your city"
                        value={city}
                        onChange={(option) => onChange({ country, state, city: option })}
                    />
                    <span className="wm-field__hint">Used to invite you to live events near you.</span>
                </div>
            )}
        </div>
    );
};
